'use client';

/**
 * "Camps near your stops" — the planner's second section, under the stops.
 *
 *   Camps near your stops
 *   ⌖ Near Port Fairy
 *   [photo]  Crags Rest Area                     (+)
 *            Free camp · 9 km away
 *   [photo]  BIG4 Port Fairy                     (+)
 *            Caravan park · 2 km away
 *
 * Works for guests: it reads the same cached all-sites list the map clusters
 * (/api/entities/map-points), so it needs no account and no routing call.
 * The nights first (where you sleep), then the destination. A site without a
 * photo gets a typed tile (tent or caravan on a tint), never a stock photo.
 */
import { PlaceImage } from './PlaceTile';
import React from 'react';
import { haversineDistanceKm } from '../../utils/tripPlanner';
import { fetchAllMapPoints } from '../../lib/searchEntities';
import type { Entity } from '../../types';
import { hasCoords } from './geo';
import { untrustedPlacePhoto } from './routeSuggest';
import { IconPin, IconPlus } from './icons';
import type { TripStop } from './types';

export interface MapPoint {
  id: number;
  name: string;
  type?: string | null;
  lat: number;
  lng: number;
  image?: string | null;
  /** Bookable online through a partner. */
  hasBooking?: boolean;
}

export interface NearbyCamp extends MapPoint {
  nearStopId: string;
  nearStopName: string;
  km: number;
}

const CAMP_TYPES: Record<string, string> = {
  free_camping: 'Free camp',
  caravan_park: 'Caravan park',
  campground: 'Campground',
  campsite: 'Campsite',
};

/** A usable photo URL. The feed also carries values like "Manual" or a bare filename. */
export function isPhotoUrl(src?: string | null): src is string {
  return typeof src === 'string' && /^https?:\/\/\S+$/.test(src);
}

/**
 * Operator photos from booking feeds are often a room or a bathroom; for a
 * caravan park that looks like a motel listing. Outdoor camps (free camps,
 * campgrounds) and our own park photos come first.
 */
export function photoRank(p: Pick<MapPoint, 'type' | 'image'>): number {
  if (!isPhotoUrl(p.image)) return 0;
  const bookingFeed = /bookeasy\.com\.au/i.test(p.image);
  const outdoor = p.type !== 'caravan_park';
  if (outdoor && !bookingFeed) return 3;
  if (!bookingFeed) return 2;
  return 1;
}

export function campTypeLabel(type?: string | null): string {
  return (type && CAMP_TYPES[type]) || 'Campsite';
}

/**
 * The stops worth suggesting a camp near: every stop you sleep at, then the
 * destination. With no nights planned yet, the middle stops stand in.
 */
export function anchorStops(stops: TripStop[], max = 3): TripStop[] {
  if (stops.length < 2) return [];
  const located = stops.map((s, i) => ({ s, i })).filter(({ s }) => hasCoords(s));
  const last = stops.length - 1;
  const nights = located.filter(({ s, i }) => i > 0 && i < last && (s.nights ?? 0) > 0);
  const middles = located.filter(({ i }) => i > 0 && i < last);
  const end = located.filter(({ i }) => i === last);
  const picked = [...(nights.length ? nights : middles), ...end];
  return picked.slice(0, max).map(({ s }) => s);
}

/**
 * Up to `perStop` camps within `radiusKm` of each anchor, best first: an
 * outdoor photo counts most (see photoRank), then closeness. A camp is offered once, and never one
 * that is already a stop.
 */
export function campsNearStops(
  points: MapPoint[],
  stops: TripStop[],
  { radiusKm = 30, perStop = 2, max = 6 }: { radiusKm?: number; perStop?: number; max?: number } = {}
): NearbyCamp[] {
  const inTrip = new Set(stops.map((s) => s.entityId).filter((id): id is number => typeof id === 'number'));
  const seen = new Set<number>();
  const out: NearbyCamp[] = [];
  for (const anchor of anchorStops(stops)) {
    if (!hasCoords(anchor)) continue;
    const near = points
      .filter((p) => !!p.type && p.type in CAMP_TYPES && !inTrip.has(p.id) && !seen.has(p.id))
      .map((p) => ({ p, km: haversineDistanceKm(anchor.lat, anchor.lng, p.lat, p.lng) }))
      .filter((c) => c.km <= radiusKm)
      .sort((a, b) => photoRank(b.p) - photoRank(a.p) || a.km - b.km)
      .slice(0, perStop);
    near.forEach(({ p, km }) => {
      seen.add(p.id);
      out.push({ ...p, nearStopId: anchor.id, nearStopName: anchor.name, km });
    });
  }
  return out.slice(0, max);
}

// One fetch per page view, shared by every planner render (and the shared
// trip page's "camps near" cards).
let pointsPromise: Promise<MapPoint[]> | null = null;
export function loadMapPoints(): Promise<MapPoint[]> {
  if (!pointsPromise) {
    pointsPromise = fetchAllMapPoints()
      .then((entities) =>
        entities
          .map((e) => {
            const a = e.addresses?.[0];
            const lat = Number(a?.gps_latitude);
            const lng = Number(a?.gps_longitude);
            return {
              id: e.id,
              name: e.name,
              type: e.entity_type,
              lat,
              lng,
              image: isPhotoUrl((e as Entity & { image_src?: string }).image_src)
                ? (e as Entity & { image_src?: string }).image_src
                : null,
              hasBooking: (e as Entity & { has_booking?: boolean }).has_booking === true,
            };
          })
          .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))
      )
      .catch(() => {
        pointsPromise = null;
        return [];
      });
  }
  return pointsPromise;
}

function pointToEntity(c: NearbyCamp): Entity {
  return {
    id: c.id,
    name: c.name,
    entity_type: c.type,
    addresses: [{ gps_latitude: String(c.lat), gps_longitude: String(c.lng) }],
    features: [],
    ...(c.image ? { image_src: c.image } : {}),
  } as unknown as Entity;
}

export default function CampsNearStops({
  stops,
  onAdd,
}: {
  stops: TripStop[];
  onAdd: (entity: Entity) => void;
}) {
  const [points, setPoints] = React.useState<MapPoint[] | null>(null);
  React.useEffect(() => {
    let live = true;
    void loadMapPoints().then((p) => {
      if (live) setPoints(p);
    });
    return () => {
      live = false;
    };
  }, []);
  const camps = React.useMemo(() => (points ? campsNearStops(points, stops) : null), [points, stops]);

  if (stops.length < 2) return null;
  if (camps && camps.length === 0) return null;

  // Grouped under the stop they are near ("Near Lakes Entrance"), the same
  // way "Worth a stop on the way" groups its ideas under their drive. Every
  // row is the same row: photo, name, what it is and how far, "+" at the
  // right edge.
  const groups: { stopId: string; stopName: string; camps: NearbyCamp[] }[] = [];
  (camps ?? []).forEach((c) => {
    const g = groups.find((x) => x.stopId === c.nearStopId);
    if (g) g.camps.push(c);
    else groups.push({ stopId: c.nearStopId, stopName: c.nearStopName, camps: [c] });
  });

  return (
    <section className="mt-5 border-0 border-t border-solid border-tp-line px-1 pt-4" aria-labelledby="camps-near-stops" data-testid="camps-near-stops">
      <h3 id="camps-near-stops" className="m-0 text-tp-base font-semibold text-tp-ink">
        Camps near your stops
      </h3>
      {camps === null ? (
        <ul className="m-0 mt-2 flex list-none flex-col gap-1 p-0" aria-hidden="true">
          {[0, 1].map((i) => (
            <li key={i} className="flex min-h-[64px] items-center gap-3 px-1">
              <span className="block h-12 w-16 flex-none rounded-tp-sm bg-tp-surface animate-tp-pulse motion-reduce:animate-none" />
              <span className="min-w-0 flex-1">
                <span className="block h-3.5 w-3/4 rounded bg-tp-surface" />
                <span className="mt-1.5 block h-3 w-1/2 rounded bg-tp-surface" />
              </span>
            </li>
          ))}
        </ul>
      ) : (
        groups.map((g) => (
          <div key={g.stopId} className="mt-2">
            <p className="m-0 flex items-center gap-1.5 px-1 text-tp-sm font-medium text-tp-muted" data-testid="camps-near-stop">
              <IconPin className="h-3.5 w-3.5 flex-none text-tp-faint" />
              <span className="truncate">Near {g.stopName}</span>
            </p>
            <ul className="m-0 mt-0.5 flex list-none flex-col p-0">
              {g.camps.map((c) => (
                <CampRow key={c.id} camp={c} onAdd={() => onAdd(pointToEntity(c))} />
              ))}
            </ul>
          </div>
        ))
      )}
    </section>
  );
}

function CampRow({ camp, onAdd }: { camp: NearbyCamp; onAdd: () => void }) {
  const label = campTypeLabel(camp.type);
  const free = camp.type === 'free_camping';
  const near = camp.km < 1 ? `In ${camp.nearStopName}` : `${Math.round(camp.km)} km from ${camp.nearStopName}`;
  // A booking-feed cabin interior under "Caravan park" is the wrong picture:
  // the drawn caravan tile says it truthfully.
  const photo = camp.image && !untrustedPlacePhoto(camp.type ?? '', camp.image) ? camp.image : null;
  return (
    <li className="animate-tp-fade motion-reduce:animate-none">
      <button
        type="button"
        onClick={onAdd}
        aria-label={`Add ${camp.name} to the trip. ${free ? 'Free camp' : label}, ${near}.`}
        className="group/camp flex min-h-[64px] w-full min-w-0 items-center gap-3 rounded-tp-sm border-0 bg-transparent px-1 py-1.5 text-left transition-colors duration-150 [@media(hover:hover)]:hover:bg-tp-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
      >
        <PlaceImage
          src={photo}
          kind={camp.type === 'caravan_park' ? 'caravan' : 'camp'}
          className="h-12 w-16 flex-none rounded-tp-sm"
        />
        <span className="min-w-0 flex-1">
          <span className="line-clamp-2 block text-tp-base font-medium leading-[19px] text-tp-ink">{camp.name}</span>
          <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 text-tp-sm leading-[18px] text-tp-muted">
            <span className={free ? 'font-medium text-tp-ok' : ''}>{free ? 'Free camp' : label}</span>
            <span aria-hidden="true" className="text-tp-faint">·</span>
            <span className="whitespace-nowrap tabular-nums">{camp.km < 1 ? 'In town' : `${Math.round(camp.km)} km away`}</span>
          </span>
        </span>
        {/* The one "+" pattern: outlined, at the row's right edge. */}
        <span
          aria-hidden="true"
          className="flex h-8 w-8 flex-none items-center justify-center rounded-full border-[1.5px] border-solid border-tp-brand bg-white text-tp-brand transition-colors duration-150 ease-tp-out [@media(hover:hover)]:group-hover/camp:bg-tp-brand [@media(hover:hover)]:group-hover/camp:text-white"
        >
          <IconPlus className="h-3.5 w-3.5" />
        </span>
      </button>
    </li>
  );
}
